import React from 'react';

import { t } from '@lingui/macro';
import styled from 'styled-components/macro';

import { Button, Caption, Input, InputPassword, Link, ParagraphMd } from '@olist/design-system';
import { tokens } from '@olist/design-system-tokens';

import { PageLayout } from 'components';
import { ButtonWrapper, Title } from 'components/general';
import { getHelpUrl } from 'helpers/i18n';
import useMixPanel from 'hooks/use-mixpanel';
import { allowedSocialLogin, webappHost } from 'utils/envs';

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const Disclaimer = styled(Container)`
  font-weight: ${tokens.fontWeight[200]};
  padding-top: ${tokens.spacing[700]};
`;

const DisclaimerCaption = styled(Caption)`
  color: ${tokens.color.gray[600]};
`;

const GroupInput = styled.div`
  margin: ${tokens.spacing[300]} 0;
`;

const DisclaimerText = () => {
  const { track } = useMixPanel(false);

  return (
    <Disclaimer>
      <DisclaimerCaption>{t`Now you use a single account to access`}</DisclaimerCaption>
      <DisclaimerCaption>
        {t`all platforms and services`}&nbsp;<b>olist</b>.
      </DisclaimerCaption>
      <DisclaimerCaption>
        {t`If you have any questions`},&nbsp;
        <Link
          href={getHelpUrl()}
          type="inline"
          target="_blank"
          onClick={() => track('Sign in - Clicked on Read more')}
        >
          {t`learn more here`}.
        </Link>
      </DisclaimerCaption>
    </Disclaimer>
  );
};

export const Login = () => {
  const { track } = useMixPanel(false);
  const ACCOUNT_API_URL = process.env.REACT_APP_ACCOUNTS_API;
  const DEFAULTS = {
    empty: '',
  };

  const urlParams = new URLSearchParams(window.location.search);

  let errorMessage = urlParams.get('error');

  const redirectUri = urlParams.get('redirect_uri') || DEFAULTS.empty;
  const scope = urlParams.get('scope') || DEFAULTS.empty;
  const responseType = urlParams.get('response_type') || DEFAULTS.empty;
  const clientId = urlParams.get('client_id') || DEFAULTS.empty;
  const state = urlParams.get('state') || DEFAULTS.empty;
  const nonce = urlParams.get('nonce') || DEFAULTS.empty;
  const codeChallenge = urlParams.get('code_challenge') || DEFAULTS.empty;
  const codeChallengeMethod = urlParams.get('code_challenge_method') || DEFAULTS.empty;

  if (clientId === DEFAULTS.empty) {
    window.location.assign(`${webappHost}/accounts/login`);
  }

  if (errorMessage === 'invalid_request') {
    errorMessage = null;
  }

  let formUrl = `${ACCOUNT_API_URL}/v1/authentication/signin`;
  let method = 'POST';

  if (allowedSocialLogin === clientId) {
    formUrl = `${ACCOUNT_API_URL}/v1/authentication/start_flow`;
    method = 'GET';
  }

  return (
    <PageLayout footer={<DisclaimerText />}>
      <section>
        <Container>
          <Title>{t`access your account`}</Title>
          <ParagraphMd>
            {t`A account`} <b>olist</b> {t`for all platforms.`}
          </ParagraphMd>
        </Container>

        <form method={method} action={formUrl} onSubmit={() => track('Sign in - Clicked on Login')}>
          {allowedSocialLogin !== clientId && (
            <>
              <GroupInput>
                <Input
                  required
                  id="email"
                  name="email"
                  label="e-mail"
                  placeholder="email@email.com"
                  type="email"
                  feedback={errorMessage ? 'error' : ''}
                  helperText={errorMessage ? t`Incorrect email or password. Try again.` : ''}
                />
              </GroupInput>
              <GroupInput>
                <InputPassword
                  required
                  id="password"
                  label={t`password`}
                  name="password"
                  feedback={errorMessage ? 'error' : ''}
                  helperText={errorMessage ? t`Incorrect email or password. Try again.` : ''}
                />
              </GroupInput>
              <Link
                // deepcode ignore DOMXSS: can't be exploited
                href={`/auth/request-recovery?clientId=${clientId}`}
                onClick={() => track('Sign in - Clicked on Forgot password')}
              >
                {t`Forgot your password?`}
              </Link>
            </>
          )}

          <input type="hidden" name="redirectUri" value={redirectUri} />
          <input type="hidden" name="state" value={state} />
          <input type="hidden" name="nonce" value={nonce} />
          <input type="hidden" name="scope" value={scope} />
          <input type="hidden" name="responseType" value={responseType} />
          <input type="hidden" name="clientId" value={clientId} />
          <input type="hidden" name="codeChallenge" value={codeChallenge} />
          <input type="hidden" name="codeChallengeMethod" value={codeChallengeMethod} />
          <ButtonWrapper>
            <Button fixed variation="primary" type="submit" size="medium" className="button-login">
              {allowedSocialLogin === clientId ? t`Login with google` : t`To enter`}
            </Button>
          </ButtonWrapper>
        </form>
      </section>
    </PageLayout>
  );
};
